import * as _ from 'underscore'

export type DictionaryKeyType = (string | number)

export class KeyValuePair<V> {
    public key: DictionaryKeyType
    public value: V

    constructor(key: DictionaryKeyType, value: V) {
        this.key = key
        this.value = value
    }

}
export enum KeyType {
    Unknown = 0,
    String = 1,
    Number = 2
}

export class Dictionary<V> {

    //#region Members

    private keyType: KeyType
    private dictionary: { [key: string]: V } = {}

    //#endregion


    //#region Ctor

    constructor(init: KeyValuePair<V>[], keyType: KeyType = KeyType.Unknown) {
        this.keyType = keyType

        if (init != null && init.length > 0) {
            init.forEach((item: KeyValuePair<V>) => {
                this.dictionary[item.key] = item.value
            })
        }
    }

    //#endregion


    //#region Add

    public add(key: DictionaryKeyType, value: V) {
        this.dictionary[key] = value
    }

    public addOrUpdate(key: DictionaryKeyType, value: V) {
        this.dictionary[key] = value
    }

    public addRange(items: V[], keyProvider: (item: V) => DictionaryKeyType): Dictionary<V> {
        if (items != null && items.length > 0 && keyProvider != null) {
            items.forEach((item: V) => {
                this.addOrUpdate(keyProvider(item), item)
            })
        }

        return this
    }

    //#endregion


    //#region Get

    public tryGetValue(key: DictionaryKeyType): V {
        if (typeof this.dictionary[key] !== 'undefined') {
            return this.dictionary[key]
        }

        return null
    }

    public tryGetTypedValue<T>(key: DictionaryKeyType): T {
        if (this.dictionary[key]) {
            return <T><any>this.dictionary[key]
        }

        return null
    }

    //#endregion


    //#region Remove

    public clear(): void {
        this.dictionary = {}
    }

    public remove(key: DictionaryKeyType): void {
        if (this.dictionary[key]) {
            delete this.dictionary[key];
        }
    }

    //#endregion


    //#region Key

    public containsKey(key: DictionaryKeyType): boolean {
        return this.dictionary != null && typeof this.dictionary[key] != 'undefined'
    }

    public keys(): DictionaryKeyType[] {
        if (this.keyType == KeyType.Number) {
            return Object.keys(this.dictionary).map(k => { return +k })
        }

        return Object.keys(this.dictionary)
    }

    public setKeyType(keyType: KeyType): void {
        this.keyType = keyType
    }

    private getKey(key: DictionaryKeyType): DictionaryKeyType {
        if (this.keyType === KeyType.Number) {
            return Number(key)
        }

        return key
    }

    //#endregion


    //#region Values

    public values(): V[] {
        const values: V[] = []

        for (let key in this.dictionary) {
            values.push(this.dictionary[key])
        }

        return values
    }

    public valuesObject(): { [key: string]: V } {
        const values: { [key: string]: V } = {}

        for (let key in this.dictionary) {
            values[key] = this.dictionary[key]
        }

        return values
    }

    //#endregion


    //#region Iteration

    public forEach(method: (key: DictionaryKeyType, value: V, index: number) => void): void {
        if (method != null) {
            let index: number = 0

            for (let key in this.dictionary) {

                method(this.getKey(key), this.dictionary[key], index)

                index += 1
            }
        }
    }

    //#endregion


    //#region Size

    public get length(): number {
        return Object.keys(this.dictionary).length
    }

    //#endregion


    //#region Restore

    public static restore<T>(objectFromLocalStorage: any) {
        const dictionary: Dictionary<T> = new Dictionary<T>(null)

        for (let key in objectFromLocalStorage.dictionary) {
            dictionary.dictionary[key] = objectFromLocalStorage.dictionary[key]
        }

        return dictionary
    }

    //#endregion


    //#region Converting

    public toObject(): Record<string, V> {
        const rowObject: Record<string, V> = {}

        this.forEach((key: DictionaryKeyType, value: V) => {
            rowObject[key] = value
        })

        return rowObject
    }

    public toArray(): KeyValuePair<V>[] {
        const list: KeyValuePair<V>[] = []

        this.forEach((key: DictionaryKeyType, value: V) => {
            list.push(new KeyValuePair(key, value))
        })

        return list
    }

    //#endregion


    //#region Factories

    public static createFromArray<V>(items: V[], keyType: KeyType, keyProvider: (item: V) => DictionaryKeyType): Dictionary<V> {
        const dict: Dictionary<V> = new Dictionary<V>(null, keyType)

        dict.addRange(items, keyProvider)

        return dict
    }

    public static createFromDictionary<V>(sourceDictionary: Dictionary<V>): Dictionary<V> {
        if (sourceDictionary == null) {
            return null
        }

        const dict: Dictionary<V> = new Dictionary<V>(null, sourceDictionary.keyType)

        sourceDictionary.forEach((key: DictionaryKeyType, value: V) => {
            dict.addOrUpdate(key, value)
        })

        return dict
    }

    public static createFromGroup<V>(items: V[], keyType: KeyType, keyProvider: (item: V) => DictionaryKeyType): Dictionary<V[]> {
        const dict: Dictionary<V[]> = new Dictionary<V[]>(null, keyType)

        if (items != null && keyProvider != null) {
            items.forEach((item: V) => {
                const key: DictionaryKeyType = keyProvider(item)

                if (!dict.containsKey(key)) {
                    dict.addOrUpdate(key, [])
                }

                dict.tryGetValue(key).push(item)
            })
        }

        return dict
    }

    public static createFromKeyValue<SV, TV>(items: SV[], keyType: KeyType, keyProvider: (item: SV) => DictionaryKeyType, valueProvider: (item: SV) => TV) {
        const dict: Dictionary<TV> = new Dictionary<TV>(null, keyType)

        if (items != null && keyProvider != null && valueProvider != null) {
            items.forEach((item: SV) => {
                const key: DictionaryKeyType = keyProvider(item)

                dict.addOrUpdate(key, valueProvider(item))
            })
        }

        return dict
    }

    public static createFromObject<V>(object: Record<string, V>) {
        const dict: Dictionary<V> = new Dictionary<V>(null, KeyType.String)

        if (object != null) {
            _
                .chain(object)
                .keys()
                .forEach((key: string) => {
                    dict.addOrUpdate(key, object[key])
                })
        }

        return dict
    }

    //#endregion
}